import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Paper,
  Typography,
  IconButton,
  Box,
  Divider,
  CircularProgress,
  Fade,
} from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from "dayjs";
import axios from "axios";
import { API_URI } from "../../../constants/GlobalConstants";
import { useSelector } from "react-redux";

const PrintScheduleModal = ({ open, onClose }) => {
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [quickRange, setQuickRange] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchPDF(dateRange.startDate, dateRange.endDate);
    }
  }, [dateRange]);

  const fetchPDF = async (startDate, endDate) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URI}/api/schedules/schedule-pdf-download`,
        { startDate, endDate },
        {
          responseType: "arraybuffer",
          headers: { Authorization: `Bearer ${userInfo.token}` },
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(blob);
      setPdfUrl(pdfUrl);
    } catch (error) {
      console.error("Error fetching PDF:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `Schedule_${dateRange.startDate}_${dateRange.endDate}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        bgcolor: 'primary.main',
        color: 'white'
        
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalendarTodayIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Print Schedule</Typography>
        </Box>
        <IconButton 
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Left: Date Selection */}
          <Grid item xs={12} md={4}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mt: 2
              }}
            >
              <Typography variant="subtitle1" fontWeight="500" gutterBottom>
                Select Date Range
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Quick Select</InputLabel>
                <Select
                  value={quickRange}
                  onChange={(e) => {
                    const range = e.target.value;
                    setQuickRange(range);

                    const now = dayjs();
                    let start, end;
                    if (range === "thisMonth") {
                      start = now.startOf("month").format("YYYY-MM-DD");
                      end = now.endOf("month").format("YYYY-MM-DD");
                    } else if (range === "lastMonth") {
                      start = now.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
                      end = now.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
                    }
                    setDateRange({ startDate: start, endDate: end });
                  }}
                  size="small"
                >
                  <MenuItem value="">Custom Range</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                  <MenuItem value="lastMonth">Last Month</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Start Date"
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              
              <TextField
                label="End Date"
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Paper>
          </Grid>

          {/* Right: PDF Preview */}
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={0}
              sx={{ 
                height: '400px',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 2
              }}
            >
              {loading ? (
                <CircularProgress />
              ) : pdfUrl ? (
                <iframe 
                  src={pdfUrl} 
                  width="100%" 
                  height="100%" 
                  style={{ border: 'none' }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <CalendarTodayIcon 
                    sx={{ 
                      fontSize: 48, 
                      color: 'text.secondary',
                      mb: 2 
                    }} 
                  />
                  <Typography color="text.secondary">
                    Select a date range to preview the schedule
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 1 }}
        >
          Cancel
        </Button>
        
        {pdfUrl && (
          <Button
            onClick={handleDownload}
            color="primary"
            variant="contained"
            startIcon={<FileDownloadIcon />}
            sx={{ borderRadius: 1 }}
          >
            Download PDF
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PrintScheduleModal;